import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { aiserverbaselink, imagebaselink } from '../../../Env_Variables';
import { Loggedincontext } from '../../../Loggedincontext';
import { isStudioAiEnabled } from './aiAllowedInstitutes';
import { aiInstituteHeaders } from './aiInstituteHeaders';
import './ImageStudio.css';

const AI_PRESETS = [
    { id: 'white-bg', label: 'White BG', prompt: 'Clean white Tabex catalog studio background. Soft natural shadow. Keep product unchanged.' },
    { id: 'enhance', label: 'Enhance', prompt: 'Sharper, balanced commercial lighting, Tabex catalog quality. Keep product exact.' },
    { id: 'clean', label: 'Clean', prompt: 'Remove clutter and distractions around the product. Keep product unchanged.' },
    { id: 'color', label: 'Color', prompt: 'Soft commercial color grade for ecommerce. Keep shape and branding exact.' },
    { id: 'shadow', label: 'Soft shadow', prompt: 'Add soft natural product shadow on a clean surface. Keep product unchanged.' },
];

const CROP_ASPECTS = [
    { id: 'free', label: 'Free', value: null },
    { id: '1:1', label: '1:1', value: 1 },
    { id: '4:5', label: '4:5', value: 4 / 5 },
    { id: '16:9', label: '16:9', value: 16 / 9 },
    { id: '9:16', label: '9:16', value: 9 / 16 },
];

const MARK_COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#a855f7', '#ffffff', '#111827'];

const DEFAULT_ADJUST = {
    brightness: 100,
    contrast: 100,
    saturate: 100,
    warmth: 0,
};

/** Max edge for AI uploads — fewer image input tokens = cheaper. */
const AI_MAX_EDGE_DRAFT = 512;
const AI_MAX_EDGE_FINAL = 1024;
const AI_JPEG_QUALITY_DRAFT = 0.65;
const AI_JPEG_QUALITY_FINAL = 0.82;

const TEXT_FONTS = [
    { id: 'system-ui, sans-serif', label: 'Sans' },
    { id: 'Georgia, serif', label: 'Serif' },
    { id: 'Impact, Haettenschweiler, sans-serif', label: 'Impact' },
    { id: '"Courier New", monospace', label: 'Mono' },
    { id: '"Arial Black", Gadget, sans-serif', label: 'Black' },
];

const TEXT_COLORS = ['#ffffff', '#111827', '#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#a855f7'];

const LOGO_CORNERS = [
    { id: 'tl', label: 'Top left', x: 12, y: 12 },
    { id: 'tr', label: 'Top right', x: 88, y: 12 },
    { id: 'bl', label: 'Bottom left', x: 12, y: 88 },
    { id: 'br', label: 'Bottom right', x: 88, y: 88 },
    { id: 'center', label: 'Center', x: 50, y: 50 },
];

async function fetchImageAsDataUrl(url) {
    const response = await fetch(`${aiserverbaselink}/instowner/ai/images/fetch`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...aiInstituteHeaders(),
        },
        body: JSON.stringify({ image_url: url }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.data_url) {
        throw new Error(data?.error || 'Could not load logo');
    }
    return data.data_url;
}

async function loadDrawableImage(src) {
    try {
        return await loadImage(src);
    } catch (_) {
        if (!src || !/^https?:\/\//i.test(src)) throw new Error('Could not load image');
        const dataUrl = await fetchImageAsDataUrl(src);
        return loadImage(dataUrl);
    }
}

function drawLogoStampsOnCtx(ctx, canvasW, canvasH, stamps, imagesBySrc) {
    for (const stamp of stamps) {
        const img = imagesBySrc.get(stamp.src);
        if (!img) continue;
        const maxW = Math.max(8, (Number(stamp.size) || 18) / 100 * canvasW);
        const scale = maxW / Math.max(1, img.naturalWidth);
        const w = img.naturalWidth * scale;
        const h = img.naturalHeight * scale;
        const x = (stamp.x / 100) * canvasW - w / 2;
        const y = (stamp.y / 100) * canvasH - h / 2;
        ctx.save();
        ctx.globalAlpha = Math.min(1, Math.max(0.05, (Number(stamp.opacity) || 100) / 100));
        ctx.drawImage(img, x, y, w, h);
        ctx.restore();
    }
}

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const isRemote = /^https?:\/\//i.test(String(src || ''));
        if (isRemote) img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Could not load image'));
        img.src = src;
    });
}

function applyWarmthFilter(ctx, width, height, warmth) {
    if (!warmth) return;
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    const amount = warmth / 100;
    for (let i = 0; i < data.length; i += 4) {
        data[i] = Math.min(255, data[i] + 30 * amount);
        data[i + 2] = Math.max(0, data[i + 2] - 20 * amount);
    }
    ctx.putImageData(imageData, 0, 0);
}

/** Downscale + JPEG compress for cheap AI edits. Draft = 512px. */
async function compressForAi(dataUrlOrSrc, mode = 'draft') {
    const img = await loadImage(dataUrlOrSrc);
    const maxEdge = mode === 'final' ? AI_MAX_EDGE_FINAL : AI_MAX_EDGE_DRAFT;
    const quality = mode === 'final' ? AI_JPEG_QUALITY_FINAL : AI_JPEG_QUALITY_DRAFT;
    const scale = Math.min(1, maxEdge / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL('image/jpeg', quality);
}

function drawTextLayersOnCtx(ctx, canvasW, canvasH, layers) {
    for (const t of layers) {
        const raw = String(t.text || '');
        if (!raw.trim()) continue;
        const fontPx = Math.max(12, Math.round((Number(t.size) || 6) / 100 * canvasW));
        ctx.save();
        ctx.font = `${t.bold ? '700' : '500'} ${fontPx}px ${t.font || 'system-ui, sans-serif'}`;
        ctx.textAlign = t.align || 'center';
        ctx.textBaseline = 'middle';
        const x = (Number(t.x) / 100) * canvasW;
        const y = (Number(t.y) / 100) * canvasH;
        const lines = raw.split('\n');
        const lineH = fontPx * 1.15;
        const startY = y - ((lines.length - 1) * lineH) / 2;
        lines.forEach((line, i) => {
            const ly = startY + i * lineH;
            if (t.stroke) {
                ctx.lineWidth = Math.max(2, fontPx * 0.1);
                ctx.strokeStyle = t.strokeColor || '#111827';
                ctx.lineJoin = 'round';
                ctx.miterLimit = 2;
                ctx.strokeText(line, x, ly);
            }
            ctx.fillStyle = t.color || '#ffffff';
            ctx.fillText(line, x, ly);
        });
        ctx.restore();
    }
}

/** Geometric Tabexawy mark — matches chat branding. */
function TabexawyMark({ className = '', size = 22 }) {
    const uid = `studioMark-${size}`;
    return (
        <svg
            className={className}
            width={size}
            height={size}
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <path
                d="M7 8.25h18c.69 0 1.25.56 1.25 1.25v1.9c0 .69-.56 1.25-1.25 1.25h-6.35v10.1c0 .69-.56 1.25-1.25 1.25h-2.8c-.69 0-1.25-.56-1.25-1.25v-10.1H7c-.69 0-1.25-.56-1.25-1.25V9.5c0-.69.56-1.25 1.25-1.25Z"
                fill="currentColor"
            />
            <path
                d="M9.2 9.9h13.6v1.15H17.1v11.05h-2.2V11.05H9.2V9.9Z"
                fill={`url(#${uid})`}
                opacity="0.35"
            />
            <circle cx="23.4" cy="9.1" r="1.55" fill="#EAC435" />
            <defs>
                <linearGradient id={uid} x1="9" y1="9" x2="24" y2="24" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#fff" />
                    <stop offset="1" stopColor="#fff" stopOpacity="0" />
                </linearGradient>
            </defs>
        </svg>
    );
}

function IconClose() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}

function IconUndo() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M9 14L4 9l5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4 9h10a6 6 0 010 12h-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}

function IconRedo() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M15 14l5-5-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20 9H10a6 6 0 000 12h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}

function formatCost(cost) {
    if (!cost) return null;
    const tokens = cost.total_tokens ?? 0;
    const display = cost.cost_usd_display || `$${(cost.cost_usd || 0).toFixed(6)}`;
    return `${tokens} tok · ${display}`;
}

/**
 * Full-screen image studio for ONE image.
 * Local tools (crop/mark/adjust) are free; AI edits are isolated + compressed for low cost.
 */
export default function ImageStudio({ open, src, alt = 'Image', onClose, onApply, allowApply = true }) {
    const { userloggedinfobjcontext } = useContext(Loggedincontext);
    const studioAiEnabled = isStudioAiEnabled(userloggedinfobjcontext?.currentinstinfo?.instid);
    const [workingSrc, setWorkingSrc] = useState(src || '');
    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [adjust, setAdjust] = useState(DEFAULT_ADJUST);
    const [rotation, setRotation] = useState(0);
    const [flipH, setFlipH] = useState(false);
    const [flipV, setFlipV] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [prompt, setPrompt] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [compare, setCompare] = useState(false);
    const [panel, setPanel] = useState('crop');
    const [lastCost, setLastCost] = useState(null);
    const [sessionCost, setSessionCost] = useState({ total_tokens: 0, cost_usd: 0 });
    const [aiMode, setAiMode] = useState('draft'); // draft = cheapest · final = sharper/costlier
    const [studioVisible, setStudioVisible] = useState(false);
    const [studioClosing, setStudioClosing] = useState(false);
    const closeTimerRef = useRef(null);

    // Crop
    const [cropAspect, setCropAspect] = useState(null);
    const [cropRect, setCropRect] = useState(null); // {x,y,w,h} in % of preview box
    const cropDragRef = useRef(null);

    // Mark / annotate
    const [markTool, setMarkTool] = useState('pen'); // pen | rect | arrow | highlight | eraser
    const [markColor, setMarkColor] = useState('#ef4444');
    const [markSize, setMarkSize] = useState(4);
    const [marks, setMarks] = useState([]);
    const markDragRef = useRef(null);

    // Text overlays (local, free)
    const [textLayers, setTextLayers] = useState([]);
    const [selectedTextId, setSelectedTextId] = useState(null);
    const [textDraft, setTextDraft] = useState('Your text');
    const [textColor, setTextColor] = useState('#ffffff');
    const [textSize, setTextSize] = useState(7);
    const [textFont, setTextFont] = useState(TEXT_FONTS[0].id);
    const [textAlign, setTextAlign] = useState('center');
    const [textBold, setTextBold] = useState(true);
    const [textStroke, setTextStroke] = useState(true);
    const textDragRef = useRef(null);

    // Logo stamp (institute logo or custom upload)
    const [logoStamps, setLogoStamps] = useState([]);
    const [selectedLogoId, setSelectedLogoId] = useState(null);
    const [logoSize, setLogoSize] = useState(18);
    const [logoOpacity, setLogoOpacity] = useState(100);
    const [customLogoSrc, setCustomLogoSrc] = useState('');
    const logoDragRef = useRef(null);
    const logoFileRef = useRef(null);

    const instituteLogoPath = userloggedinfobjcontext?.currentinstinfo?.instlogo || '';
    const instituteLogoUrl = instituteLogoPath
        ? /^https?:\/\//i.test(instituteLogoPath)
            ? instituteLogoPath
            : `${imagebaselink}${instituteLogoPath}`
        : '';
    const activeLogoSrc = customLogoSrc || instituteLogoUrl;

    const stageRef = useRef(null);
    const overlayRef = useRef(null);

    const originalSrcRef = useRef(src || '');

    const historyIndexRef = useRef(historyIndex);
    historyIndexRef.current = historyIndex;

    const CLOSE_MS = 420;

    useEffect(() => {
        if (open) {
            if (closeTimerRef.current) {
                clearTimeout(closeTimerRef.current);
                closeTimerRef.current = null;
            }
            setStudioVisible(true);
            setStudioClosing(false);
            return undefined;
        }
        if (!studioVisible) return undefined;
        setStudioClosing(true);
        const reduceMotion =
            typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        const delay = reduceMotion ? 0 : CLOSE_MS;
        closeTimerRef.current = setTimeout(() => {
            setStudioVisible(false);
            setStudioClosing(false);
            closeTimerRef.current = null;
        }, delay);
        return () => {
            if (closeTimerRef.current) {
                clearTimeout(closeTimerRef.current);
                closeTimerRef.current = null;
            }
        };
    }, [open, studioVisible]);

    const requestClose = useCallback(() => {
        if (busy || studioClosing) return;
        onClose?.();
    }, [busy, studioClosing, onClose]);

    const pushHistory = useCallback((nextSrc, label = 'Edit', cost = null) => {
        setHistory((prev) => {
            const idx = Math.max(0, Math.min(historyIndexRef.current, prev.length - 1));
            const base = prev.slice(0, idx + 1);
            const next = [...base, { src: nextSrc, label, at: Date.now(), cost }].slice(-16);
            historyIndexRef.current = next.length - 1;
            setHistoryIndex(next.length - 1);
            return next;
        });
        setWorkingSrc(nextSrc);
        if (cost) {
            setLastCost(cost);
            setSessionCost((s) => ({
                total_tokens: (s.total_tokens || 0) + (cost.total_tokens || 0),
                cost_usd: Number(((s.cost_usd || 0) + (cost.cost_usd || 0)).toFixed(8)),
            }));
        }
    }, []);

    const resetLocalTools = () => {
        setAdjust(DEFAULT_ADJUST);
        setRotation(0);
        setFlipH(false);
        setFlipV(false);
        setZoom(1);
        setCropRect(null);
        setMarks([]);
        setTextLayers([]);
        setSelectedTextId(null);
        setLogoStamps([]);
        setSelectedLogoId(null);
        setCustomLogoSrc('');
    };

    useEffect(() => {
        if (!open || !src) return;
        let cancelled = false;
        originalSrcRef.current = src;
        setWorkingSrc(src);
        setHistory([{ src, label: 'Original', at: Date.now() }]);
        setHistoryIndex(0);
        resetLocalTools();
        setPrompt('');
        setError('');
        setBusy(false);
        setCompare(false);
        setPanel(studioAiEnabled ? 'ai' : 'crop');
        setLastCost(null);
        setSessionCost({ total_tokens: 0, cost_usd: 0 });
        setAiMode('draft');
        setCropAspect(null);

        (async () => {
            if (!/^https?:\/\//i.test(src)) return;
            try {
                const response = await fetch(`${aiserverbaselink}/instowner/ai/images/fetch`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...aiInstituteHeaders(),
                    },
                    body: JSON.stringify({ image_url: src }),
                });
                const data = await response.json().catch(() => ({}));
                if (cancelled || !response.ok || !data?.data_url) return;
                setWorkingSrc(data.data_url);
                setHistory([{ src: data.data_url, label: 'Original', at: Date.now() }]);
                setHistoryIndex(0);
            } catch (_) {
                /* keep remote src */
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, src, studioAiEnabled]);

    useEffect(() => {
        if (!studioVisible) return;
        const onKey = (e) => {
            if (e.key === 'Escape' && !busy && !studioClosing) {
                if ((panel === 'text' && selectedTextId) || (panel === 'logo' && selectedLogoId)) {
                    setSelectedTextId(null);
                    setSelectedLogoId(null);
                    return;
                }
                requestClose();
            }
            if ((panel === 'text' || panel === 'logo') && (e.key === 'Delete' || e.key === 'Backspace')) {
                const tag = (e.target?.tagName || '').toLowerCase();
                if (tag === 'textarea' || tag === 'input') return;
                if (panel === 'text' && selectedTextId) {
                    e.preventDefault();
                    setTextLayers((prev) => prev.filter((t) => t.id !== selectedTextId));
                    setSelectedTextId(null);
                }
                if (panel === 'logo' && selectedLogoId) {
                    e.preventDefault();
                    setLogoStamps((prev) => prev.filter((s) => s.id !== selectedLogoId));
                    setSelectedLogoId(null);
                }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [studioVisible, busy, requestClose, panel, selectedTextId, selectedLogoId]);

    const filterCss = useMemo(() => {
        const warmthHue = adjust.warmth * 0.15;
        return `brightness(${adjust.brightness}%) contrast(${adjust.contrast}%) saturate(${adjust.saturate}%) sepia(${Math.max(0, adjust.warmth) * 0.35}%) hue-rotate(${warmthHue}deg)`;
    }, [adjust]);

    const transformCss = useMemo(() => {
        const sx = flipH ? -1 : 1;
        const sy = flipV ? -1 : 1;
        return `scale(${zoom * sx}, ${zoom * sy}) rotate(${rotation}deg)`;
    }, [flipH, flipV, rotation, zoom]);

    const renderBaseToCanvas = useCallback(async () => {
        const img = await loadImage(workingSrc);
        const rad = ((rotation % 360) + 360) % 360;
        const swap = rad === 90 || rad === 270;
        const w = swap ? img.naturalHeight : img.naturalWidth;
        const h = swap ? img.naturalWidth : img.naturalHeight;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.save();
        ctx.translate(w / 2, h / 2);
        ctx.rotate((rad * Math.PI) / 180);
        ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
        ctx.filter = `brightness(${adjust.brightness}%) contrast(${adjust.contrast}%) saturate(${adjust.saturate}%)`;
        ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
        ctx.restore();
        if (adjust.warmth) applyWarmthFilter(ctx, w, h, adjust.warmth);
        return canvas;
    }, [workingSrc, rotation, flipH, flipV, adjust]);

    const renderToDataUrl = useCallback(async () => {
        const canvas = await renderBaseToCanvas();
        return canvas.toDataURL('image/png');
    }, [renderBaseToCanvas]);

    const bakeAdjustments = useCallback(async () => {
        const dirty =
            adjust.brightness !== 100 ||
            adjust.contrast !== 100 ||
            adjust.saturate !== 100 ||
            adjust.warmth !== 0 ||
            rotation !== 0 ||
            flipH ||
            flipV;
        if (!dirty) return workingSrc;
        const baked = await renderToDataUrl();
        setAdjust(DEFAULT_ADJUST);
        setRotation(0);
        setFlipH(false);
        setFlipV(false);
        setZoom(1);
        pushHistory(baked, 'Adjustments');
        return baked;
    }, [adjust, rotation, flipH, flipV, workingSrc, renderToDataUrl, pushHistory]);

    const getStagePointPct = (clientX, clientY) => {
        const stage = stageRef.current;
        if (!stage) return null;
        const rect = stage.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;
        return {
            x: Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)),
            y: Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100)),
        };
    };

    // —— Crop handlers ——
    const onCropPointerDown = (e) => {
        if (panel !== 'crop' || busy) return;
        e.preventDefault();
        const p = getStagePointPct(e.clientX, e.clientY);
        if (!p) return;
        cropDragRef.current = { x0: p.x, y0: p.y };
        setCropRect({ x: p.x, y: p.y, w: 0, h: 0 });
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };

    const onCropPointerMove = (e) => {
        const drag = cropDragRef.current;
        if (!drag) return;
        const p = getStagePointPct(e.clientX, e.clientY);
        if (!p) return;
        let x = Math.min(drag.x0, p.x);
        let y = Math.min(drag.y0, p.y);
        let w = Math.abs(p.x - drag.x0);
        let h = Math.abs(p.y - drag.y0);
        if (cropAspect) {
            const stage = stageRef.current?.getBoundingClientRect();
            const pxRatio = stage ? stage.width / stage.height : 1;
            const target = cropAspect / pxRatio; // convert image aspect to % box aspect
            if (w / Math.max(h, 0.001) > target) {
                w = h * target;
                x = p.x < drag.x0 ? drag.x0 - w : drag.x0;
            } else {
                h = w / target;
                y = p.y < drag.y0 ? drag.y0 - h : drag.y0;
            }
        }
        setCropRect({ x, y, w, h });
    };

    const onCropPointerUp = () => {
        cropDragRef.current = null;
    };

    const applyCrop = async () => {
        if (!cropRect || cropRect.w < 2 || cropRect.h < 2) {
            setError('Drag a crop area first.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            const base = await bakeAdjustments();
            const img = await loadImage(base);
            const sx = (cropRect.x / 100) * img.naturalWidth;
            const sy = (cropRect.y / 100) * img.naturalHeight;
            const sw = (cropRect.w / 100) * img.naturalWidth;
            const sh = (cropRect.h / 100) * img.naturalHeight;
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(sw));
            canvas.height = Math.max(1, Math.round(sh));
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
            const next = canvas.toDataURL('image/png');
            setCropRect(null);
            pushHistory(next, 'Crop');
        } catch (err) {
            setError(err?.message || 'Crop failed');
        } finally {
            setBusy(false);
        }
    };

    // —— Mark handlers ——
    const onMarkPointerDown = (e) => {
        if (panel !== 'mark' || busy) return;
        e.preventDefault();
        const p = getStagePointPct(e.clientX, e.clientY);
        if (!p) return;
        const id = `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        markDragRef.current = { id, start: p };
        if (markTool === 'pen' || markTool === 'highlight' || markTool === 'eraser') {
            setMarks((prev) => [
                ...prev,
                {
                    id,
                    tool: markTool,
                    color: markTool === 'eraser' ? '#000' : markColor,
                    size: markSize,
                    points: [p],
                },
            ]);
        } else {
            setMarks((prev) => [
                ...prev,
                {
                    id,
                    tool: markTool,
                    color: markColor,
                    size: markSize,
                    x1: p.x,
                    y1: p.y,
                    x2: p.x,
                    y2: p.y,
                },
            ]);
        }
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };

    const onMarkPointerMove = (e) => {
        const drag = markDragRef.current;
        if (!drag) return;
        const p = getStagePointPct(e.clientX, e.clientY);
        if (!p) return;
        setMarks((prev) =>
            prev.map((m) => {
                if (m.id !== drag.id) return m;
                if (m.points) return { ...m, points: [...m.points, p] };
                return { ...m, x2: p.x, y2: p.y };
            }),
        );
    };

    const onMarkPointerUp = () => {
        markDragRef.current = null;
    };

    const bakeMarksOnto = async (baseSrc, markList) => {
        if (!markList?.length) return baseSrc;
        const img = await loadImage(baseSrc);
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const toX = (pct) => (pct / 100) * canvas.width;
        const toY = (pct) => (pct / 100) * canvas.height;

        for (const m of markList) {
            const scale = canvas.width / 400;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.lineWidth = Math.max(1, m.size * scale);
            if (m.tool === 'eraser') {
                ctx.globalCompositeOperation = 'destination-out';
                ctx.strokeStyle = 'rgba(0,0,0,1)';
            } else if (m.tool === 'highlight') {
                ctx.globalCompositeOperation = 'source-over';
                ctx.strokeStyle = m.color;
                ctx.globalAlpha = 0.35;
                ctx.lineWidth = Math.max(8, m.size * scale * 3);
            } else {
                ctx.globalCompositeOperation = 'source-over';
                ctx.strokeStyle = m.color;
                ctx.globalAlpha = 1;
                ctx.fillStyle = m.color;
            }

            if (m.points?.length) {
                ctx.beginPath();
                m.points.forEach((pt, i) => {
                    const x = toX(pt.x);
                    const y = toY(pt.y);
                    if (i === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                });
                ctx.stroke();
            } else if (m.tool === 'rect') {
                const x = toX(Math.min(m.x1, m.x2));
                const y = toY(Math.min(m.y1, m.y2));
                const w = toX(Math.abs(m.x2 - m.x1));
                const h = toY(Math.abs(m.y2 - m.y1));
                ctx.strokeRect(x, y, w, h);
            } else if (m.tool === 'arrow') {
                const x1 = toX(m.x1);
                const y1 = toY(m.y1);
                const x2 = toX(m.x2);
                const y2 = toY(m.y2);
                const angle = Math.atan2(y2 - y1, x2 - x1);
                const head = 12 * scale;
                ctx.beginPath();
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(x2, y2);
                ctx.lineTo(x2 - head * Math.cos(angle - 0.4), y2 - head * Math.sin(angle - 0.4));
                ctx.lineTo(x2 - head * Math.cos(angle + 0.4), y2 - head * Math.sin(angle + 0.4));
                ctx.closePath();
                ctx.fill();
            }
            ctx.globalAlpha = 1;
            ctx.globalCompositeOperation = 'source-over';
        }

        return canvas.toDataURL('image/png');
    };

    const bakeMarks = async () => {
        if (!marks.length) {
            setError('Draw a mark first.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            const base = await bakeAdjustments();
            const next = await bakeMarksOnto(base, marks);
            setMarks([]);
            pushHistory(next, 'Marks');
        } catch (err) {
            setError(err?.message || 'Could not apply marks');
        } finally {
            setBusy(false);
        }
    };

    const selectedText = useMemo(
        () => textLayers.find((t) => t.id === selectedTextId) || null,
        [textLayers, selectedTextId],
    );

    const syncSelectedTextFields = (layer) => {
        if (!layer) return;
        setTextDraft(layer.text);
        setTextColor(layer.color);
        setTextSize(layer.size);
        setTextFont(layer.font);
        setTextAlign(layer.align);
        setTextBold(Boolean(layer.bold));
        setTextStroke(Boolean(layer.stroke));
    };

    const updateSelectedText = (patch) => {
        if (!selectedTextId) return;
        setTextLayers((prev) => prev.map((t) => (t.id === selectedTextId ? { ...t, ...patch } : t)));
    };

    const onTextPointerDown = (e) => {
        if (panel !== 'text' || busy) return;
        e.preventDefault();
        const p = getStagePointPct(e.clientX, e.clientY);
        if (!p) return;

        // Hit-test existing text near click (±6%)
        const hit = [...textLayers].reverse().find((t) => Math.abs(t.x - p.x) < 8 && Math.abs(t.y - p.y) < 6);
        if (hit) {
            setSelectedTextId(hit.id);
            syncSelectedTextFields(hit);
            textDragRef.current = { id: hit.id, ox: p.x - hit.x, oy: p.y - hit.y };
            e.currentTarget.setPointerCapture?.(e.pointerId);
            return;
        }

        const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const layer = {
            id,
            text: textDraft || 'Your text',
            x: p.x,
            y: p.y,
            color: textColor,
            size: textSize,
            font: textFont,
            align: textAlign,
            bold: textBold,
            stroke: textStroke,
            strokeColor: textColor === '#ffffff' ? '#111827' : '#ffffff',
        };
        setTextLayers((prev) => [...prev, layer]);
        setSelectedTextId(id);
        textDragRef.current = { id, ox: 0, oy: 0 };
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };

    const onTextPointerMove = (e) => {
        const drag = textDragRef.current;
        if (!drag) return;
        const p = getStagePointPct(e.clientX, e.clientY);
        if (!p) return;
        const x = Math.min(98, Math.max(2, p.x - drag.ox));
        const y = Math.min(98, Math.max(2, p.y - drag.oy));
        setTextLayers((prev) => prev.map((t) => (t.id === drag.id ? { ...t, x, y } : t)));
    };

    const onTextPointerUp = () => {
        textDragRef.current = null;
    };

    const bakeTextOnto = async (baseSrc, layers) => {
        if (!layers?.length) return baseSrc;
        const drawableLayers = layers.filter((t) => String(t.text || '').trim());
        if (!drawableLayers.length) return baseSrc;

        let img = await loadDrawableImage(baseSrc);
        let canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        let ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        try {
            drawTextLayersOnCtx(ctx, canvas.width, canvas.height, drawableLayers);
            return canvas.toDataURL('image/png');
        } catch (_) {
            // Remote image may load but taint the canvas — refetch as data URL and retry.
            if (!/^https?:\/\//i.test(baseSrc)) throw new Error('Could not bake text');
            const dataUrl = await fetchImageAsDataUrl(baseSrc);
            img = await loadImage(dataUrl);
            canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth || img.width;
            canvas.height = img.naturalHeight || img.height;
            ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            drawTextLayersOnCtx(ctx, canvas.width, canvas.height, drawableLayers);
            return canvas.toDataURL('image/png');
        }
    };

    const bakeText = async () => {
        if (!textLayers.length) {
            setError('Add text on the image first (click to place).');
            return;
        }
        setBusy(true);
        setError('');
        try {
            const base = await bakeAdjustments();
            const next = await bakeTextOnto(base, textLayers);
            setTextLayers([]);
            setSelectedTextId(null);
            pushHistory(next, 'Text');
        } catch (err) {
            setError(err?.message || 'Could not bake text');
        } finally {
            setBusy(false);
        }
    };

    const selectedLogo = useMemo(
        () => logoStamps.find((s) => s.id === selectedLogoId) || null,
        [logoStamps, selectedLogoId],
    );

    const updateSelectedLogo = (patch) => {
        if (!selectedLogoId) return;
        setLogoStamps((prev) => prev.map((s) => (s.id === selectedLogoId ? { ...s, ...patch } : s)));
    };

    const placeLogoStamp = (corner = LOGO_CORNERS[3]) => {
        if (!activeLogoSrc) {
            setError('Upload a logo or set your institute logo in settings first.');
            return;
        }
        const id = `logo-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        setLogoStamps((prev) => [
            ...prev,
            {
                id,
                src: activeLogoSrc,
                x: corner.x,
                y: corner.y,
                size: logoSize,
                opacity: logoOpacity,
            },
        ]);
        setSelectedLogoId(id);
        setError('');
        setPanel('logo');
    };

    const onLogoPointerDown = (e) => {
        if (panel !== 'logo' || busy) return;
        e.preventDefault();
        const p = getStagePointPct(e.clientX, e.clientY);
        if (!p) return;

        const hit = [...logoStamps].reverse().find((s) => {
            const half = Math.max(6, (Number(s.size) || 18) / 2);
            return Math.abs(s.x - p.x) < half && Math.abs(s.y - p.y) < half;
        });
        if (hit) {
            setSelectedLogoId(hit.id);
            setLogoSize(hit.size);
            setLogoOpacity(hit.opacity);
            logoDragRef.current = { id: hit.id, ox: p.x - hit.x, oy: p.y - hit.y };
            e.currentTarget.setPointerCapture?.(e.pointerId);
            return;
        }

        if (!activeLogoSrc) {
            setError('No logo available to stamp.');
            return;
        }
        const id = `logo-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        setLogoStamps((prev) => [
            ...prev,
            {
                id,
                src: activeLogoSrc,
                x: p.x,
                y: p.y,
                size: logoSize,
                opacity: logoOpacity,
            },
        ]);
        setSelectedLogoId(id);
        logoDragRef.current = { id, ox: 0, oy: 0 };
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };

    const onLogoPointerMove = (e) => {
        const drag = logoDragRef.current;
        if (!drag) return;
        const p = getStagePointPct(e.clientX, e.clientY);
        if (!p) return;
        const x = Math.min(96, Math.max(4, p.x - drag.ox));
        const y = Math.min(96, Math.max(4, p.y - drag.oy));
        setLogoStamps((prev) => prev.map((s) => (s.id === drag.id ? { ...s, x, y } : s)));
    };

    const onLogoPointerUp = () => {
        logoDragRef.current = null;
    };

    const bakeLogoOnto = async (baseSrc, stamps) => {
        if (!stamps?.length) return baseSrc;
        const img = await loadImage(baseSrc);
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imagesBySrc = new Map();
        for (const stamp of stamps) {
            if (imagesBySrc.has(stamp.src)) continue;
            imagesBySrc.set(stamp.src, await loadDrawableImage(stamp.src));
        }
        drawLogoStampsOnCtx(ctx, canvas.width, canvas.height, stamps, imagesBySrc);
        return canvas.toDataURL('image/png');
    };

    const bakeLogo = async () => {
        if (!logoStamps.length) {
            setError('Place a logo stamp on the image first.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            const base = await bakeAdjustments();
            const next = await bakeLogoOnto(base, logoStamps);
            setLogoStamps([]);
            setSelectedLogoId(null);
            pushHistory(next, 'Logo stamp');
        } catch (err) {
            setError(err?.message || 'Could not bake logo');
        } finally {
            setBusy(false);
        }
    };

    const onCustomLogoFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!/^image\//i.test(file.type)) {
            setError('Please choose an image file for the logo stamp.');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = String(reader.result || '');
            if (!dataUrl) return;
            setCustomLogoSrc(dataUrl);
            setError('');
        };
        reader.onerror = () => setError('Could not read logo file');
        reader.readAsDataURL(file);
    };

    const runAiEdit = useCallback(
        async (promptOverride) => {
            const instruction = String(promptOverride || prompt || '')
                .replace(/\s+/g, ' ')
                .trim()
                .slice(0, aiMode === 'draft' ? 180 : 400);
            if (!instruction) {
                setError('Write a short edit instruction.');
                return;
            }
            setBusy(true);
            setError('');
            try {
                let sourceForAi = await bakeAdjustments();
                if (marks.length) {
                    sourceForAi = await bakeMarksOnto(sourceForAi, marks);
                    setMarks([]);
                    pushHistory(sourceForAi, 'Marks');
                }
                // Keep text/logo overlays local — baking them before AI lets the model wipe them.
                const cheap = await compressForAi(sourceForAi, aiMode);
                const response = await fetch(`${aiserverbaselink}/instowner/ai/images/edit`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...aiInstituteHeaders(),
                    },
                    body: JSON.stringify({
                        prompt: instruction,
                        image_base64: cheap,
                        quality: 'low',
                        size: '1024x1024',
                        mode: aiMode,
                    }),
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok || !data?.ok) {
                    throw new Error(data?.error || `Edit failed (${response.status})`);
                }
                const next = data.data_url || `data:${data.content_type || 'image/png'};base64,${data.base64}`;
                const label =
                    data.fallback_from === 'gemini'
                        ? `AI ${aiMode} (OpenAI fallback)`
                        : `AI ${aiMode}`;
                pushHistory(next, label, data.cost || null);
                if (data.fallback_reason) {
                    setError(`Note: ${data.fallback_reason}`);
                } else {
                    setError('');
                }
                setPrompt('');
            } catch (err) {
                setError(err?.message || 'AI edit failed');
            } finally {
                setBusy(false);
            }
        },
        [prompt, marks, bakeAdjustments, pushHistory, aiMode],
    );

    const undo = () => {
        if (historyIndex <= 0) return;
        const next = historyIndex - 1;
        setHistoryIndex(next);
        setWorkingSrc(history[next].src);
        resetLocalTools();
    };

    const redo = () => {
        if (historyIndex >= history.length - 1) return;
        const next = historyIndex + 1;
        setHistoryIndex(next);
        setWorkingSrc(history[next].src);
        resetLocalTools();
    };

    const resetAll = () => {
        const original = originalSrcRef.current;
        setHistory([{ src: original, label: 'Original', at: Date.now() }]);
        setHistoryIndex(0);
        setWorkingSrc(original);
        resetLocalTools();
        setError('');
        setPrompt('');
        setLastCost(null);
        setSessionCost({ total_tokens: 0, cost_usd: 0 });
    };

    const handleApply = async () => {
        setBusy(true);
        setError('');
        try {
            let finalSrc = await bakeAdjustments();
            if (marks.length) {
                finalSrc = await bakeMarksOnto(finalSrc, marks);
                setMarks([]);
            }
            if (textLayers.length) {
                finalSrc = await bakeTextOnto(finalSrc, textLayers);
                setTextLayers([]);
                setSelectedTextId(null);
            }
            if (logoStamps.length) {
                finalSrc = await bakeLogoOnto(finalSrc, logoStamps);
                setLogoStamps([]);
                setSelectedLogoId(null);
            }
            onApply?.({
                originalSrc: originalSrcRef.current,
                editedSrc: finalSrc,
                alt,
            });
            onClose?.();
        } catch (err) {
            setError(err?.message || 'Could not apply image');
        } finally {
            setBusy(false);
        }
    };

    const downloadCurrent = async () => {
        try {
            let dataUrl = await renderToDataUrl();
            if (marks.length) dataUrl = await bakeMarksOnto(dataUrl, marks);
            if (textLayers.length) dataUrl = await bakeTextOnto(dataUrl, textLayers);
            if (logoStamps.length) dataUrl = await bakeLogoOnto(dataUrl, logoStamps);
            const a = document.createElement('a');
            a.href = dataUrl;
            a.download = `tabex-studio-${Date.now()}.png`;
            a.click();
        } catch (err) {
            setError(err?.message || 'Download failed');
        }
    };

    // Draw mark overlay SVG
    const markSvg = useMemo(() => {
        if (!marks.length && panel !== 'mark') return null;
        return (
            <svg className="img-studio-mark-layer" viewBox="0 0 100 100" preserveAspectRatio="none">
                {marks.map((m) => {
                    if (m.points?.length) {
                        const d = m.points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
                        return (
                            <path
                                key={m.id}
                                d={d}
                                fill="none"
                                stroke={m.tool === 'eraser' ? 'rgba(255,255,255,0.35)' : m.color}
                                strokeWidth={(m.tool === 'highlight' ? m.size * 0.8 : m.size * 0.25)}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                opacity={m.tool === 'highlight' ? 0.45 : 1}
                                vectorEffect="non-scaling-stroke"
                            />
                        );
                    }
                    if (m.tool === 'rect') {
                        return (
                            <rect
                                key={m.id}
                                x={Math.min(m.x1, m.x2)}
                                y={Math.min(m.y1, m.y2)}
                                width={Math.abs(m.x2 - m.x1)}
                                height={Math.abs(m.y2 - m.y1)}
                                fill="none"
                                stroke={m.color}
                                strokeWidth={m.size * 0.25}
                                vectorEffect="non-scaling-stroke"
                            />
                        );
                    }
                    if (m.tool === 'arrow') {
                        return (
                            <line
                                key={m.id}
                                x1={m.x1}
                                y1={m.y1}
                                x2={m.x2}
                                y2={m.y2}
                                stroke={m.color}
                                strokeWidth={m.size * 0.25}
                                markerEnd="url(#img-studio-arrow)"
                                vectorEffect="non-scaling-stroke"
                            />
                        );
                    }
                    return null;
                })}
                <defs>
                    <marker id="img-studio-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                        <path d="M0,0 L6,3 L0,6 Z" fill="currentColor" />
                    </marker>
                </defs>
            </svg>
        );
    }, [marks, panel]);

    if (!studioVisible) return null;

    const previewSrc = compare ? history[0]?.src || originalSrcRef.current : workingSrc;
    const interactionMode = panel === 'crop' || panel === 'mark' || panel === 'text';
    const studioTabs = studioAiEnabled
        ? [
              ['ai', 'AI'],
              ['crop', 'Crop'],
              ['mark', 'Mark'],
              ['text', 'Text'],
              ['adjust', 'Adjust'],
              ['transform', 'Xform'],
          ]
        : [
              ['crop', 'Crop'],
              ['mark', 'Mark'],
              ['text', 'Text'],
              ['adjust', 'Adjust'],
              ['transform', 'Xform'],
          ];

    return createPortal(
        <div
            className={`img-studio${studioClosing ? ' is-closing' : ' is-opening'}`}
            role="dialog"
            aria-modal="true"
            aria-label="Tabex Image Studio"
        >
            <div className="img-studio-backdrop" onClick={requestClose} />
            <div className="img-studio-shell">
                <header className="img-studio-header">
                    <div className="img-studio-brand">
                        <div className="img-studio-brand-mark" aria-hidden="true">
                            <TabexawyMark size={20} />
                        </div>
                        <div className="img-studio-title">
                            <span className="img-studio-kicker">
                                Tabex Studio <span className="img-studio-beta">Beta</span>
                            </span>
                            <strong>{alt || 'Product image'}</strong>
                            <span className="img-studio-note">
                                {studioAiEnabled
                                    ? 'Built for Tabex catalogs · local tools free · AI edits stay in this studio'
                                    : 'Built for Tabex catalogs · crop, mark, text, and adjust — free local tools'}
                            </span>
                        </div>
                    </div>
                    <div className="img-studio-header-actions">
                        <button type="button" className="img-studio-icon-btn" onClick={undo} disabled={busy || historyIndex <= 0 || studioClosing} title="Undo">
                            <IconUndo />
                        </button>
                        <button type="button" className="img-studio-icon-btn" onClick={redo} disabled={busy || historyIndex >= history.length - 1 || studioClosing} title="Redo">
                            <IconRedo />
                        </button>
                        <button type="button" className="img-studio-ghost-btn" onClick={resetAll} disabled={busy || studioClosing}>
                            Reset
                        </button>
                        <button type="button" className="img-studio-icon-btn" onClick={requestClose} aria-label="Close" disabled={studioClosing}>
                            <IconClose />
                        </button>
                    </div>
                </header>

                <div className="img-studio-body">
                    <div className="img-studio-canvas-wrap">
                        <div className={`img-studio-canvas-stage${interactionMode ? ' is-interactive' : ''}`}>
                            <div
                                className="img-studio-frame"
                                ref={stageRef}
                                onPointerDown={
                                    panel === 'crop'
                                        ? onCropPointerDown
                                        : panel === 'mark'
                                          ? onMarkPointerDown
                                          : panel === 'text'
                                            ? onTextPointerDown
                                            : undefined
                                }
                                onPointerMove={
                                    panel === 'crop'
                                        ? onCropPointerMove
                                        : panel === 'mark'
                                          ? onMarkPointerMove
                                          : panel === 'text'
                                            ? onTextPointerMove
                                            : undefined
                                }
                                onPointerUp={
                                    panel === 'crop'
                                        ? onCropPointerUp
                                        : panel === 'mark'
                                          ? onMarkPointerUp
                                          : panel === 'text'
                                            ? onTextPointerUp
                                            : undefined
                                }
                            >
                                <img
                                    className="img-studio-preview"
                                    src={previewSrc}
                                    alt={alt || 'Studio preview'}
                                    style={{
                                        filter: compare ? 'none' : filterCss,
                                        transform: compare ? 'none' : transformCss,
                                        pointerEvents: 'none',
                                    }}
                                    draggable={false}
                                />
                                {markSvg}
                                {(panel === 'text' || textLayers.length > 0) && (
                                    <div className="img-studio-text-layer">
                                        {textLayers.map((t) => (
                                            <div
                                                key={t.id}
                                                className={`img-studio-text-item${t.id === selectedTextId ? ' is-selected' : ''}`}
                                                style={{
                                                    left: `${t.x}%`,
                                                    top: `${t.y}%`,
                                                    color: t.color,
                                                    fontFamily: t.font,
                                                    fontWeight: t.bold ? 700 : 500,
                                                    fontSize: `${Math.max(14, Math.round((Number(t.size) || 6) * 2.4))}px`,
                                                    textAlign: t.align,
                                                    WebkitTextStroke: t.stroke
                                                        ? `1px ${t.strokeColor || '#111'}`
                                                        : '0',
                                                    transform:
                                                        t.align === 'left'
                                                            ? 'translate(0, -50%)'
                                                            : t.align === 'right'
                                                              ? 'translate(-100%, -50%)'
                                                              : 'translate(-50%, -50%)',
                                                }}
                                            >
                                                {t.text}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {panel === 'crop' && cropRect && (
                                    <div
                                        className="img-studio-crop-rect"
                                        style={{
                                            left: `${cropRect.x}%`,
                                            top: `${cropRect.y}%`,
                                            width: `${cropRect.w}%`,
                                            height: `${cropRect.h}%`,
                                        }}
                                    />
                                )}
                            </div>
                            {busy && (
                                <div className="img-studio-busy">
                                    <div className="img-studio-spinner" />
                                    <span>Tabexawy is working…</span>
                                </div>
                            )}
                        </div>

                        <div className="img-studio-versions">
                            {history.map((item, idx) => (
                                <button
                                    key={`${item.at}-${idx}`}
                                    type="button"
                                    className={`img-studio-version${idx === historyIndex ? ' is-active' : ''}`}
                                    disabled={busy}
                                    onClick={() => {
                                        setHistoryIndex(idx);
                                        setWorkingSrc(item.src);
                                        resetLocalTools();
                                        if (item.cost) setLastCost(item.cost);
                                    }}
                                    title={item.cost ? `${item.label} · ${formatCost(item.cost)}` : item.label}
                                >
                                    <img src={item.src} alt="" />
                                    <span>{item.label}</span>
                                    {item.cost ? <em>{item.cost.cost_usd_display}</em> : null}
                                </button>
                            ))}
                        </div>
                    </div>

                    <aside className="img-studio-side">
                        <div className={`img-studio-tabs ${studioAiEnabled ? 'img-studio-tabs-6' : 'img-studio-tabs-5'}`}>
                            {studioTabs.map(([id, label]) => (
                                <button
                                    key={id}
                                    type="button"
                                    className={panel === id ? 'is-active' : ''}
                                    onClick={() => setPanel(id)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        {studioAiEnabled && panel === 'ai' && (
                            <div className="img-studio-panel">
                                <div className="img-studio-ai-intro">
                                    <div className="img-studio-ai-intro-mark">
                                        <TabexawyMark size={18} />
                                    </div>
                                    <div>
                                        <strong>Ask Tabexawy</strong>
                                        <p>
                                            Describe the catalog look you want. Draft is cheapest (512px); switch to Final when you’re ready to publish.
                                        </p>
                                    </div>
                                </div>
                                <div className="img-studio-presets">
                                    <button
                                        type="button"
                                        className={`img-studio-chip${aiMode === 'draft' ? ' is-on' : ''}`}
                                        disabled={busy}
                                        onClick={() => setAiMode('draft')}
                                    >
                                        Draft · cheapest
                                    </button>
                                    <button
                                        type="button"
                                        className={`img-studio-chip${aiMode === 'final' ? ' is-on' : ''}`}
                                        disabled={busy}
                                        onClick={() => setAiMode('final')}
                                    >
                                        Final · sharper
                                    </button>
                                </div>
                                <div className="img-studio-cost-box">
                                    <div>
                                        <span>Last AI edit</span>
                                        <strong>{lastCost ? formatCost(lastCost) : '—'}</strong>
                                    </div>
                                    <div>
                                        <span>Session total</span>
                                        <strong>
                                            {sessionCost.total_tokens || 0} tok · $
                                            {(sessionCost.cost_usd || 0).toFixed(6)}
                                        </strong>
                                    </div>
                                </div>
                                <div className="img-studio-presets">
                                    {AI_PRESETS.map((p) => (
                                        <button
                                            key={p.id}
                                            type="button"
                                            className="img-studio-chip"
                                            disabled={busy}
                                            onClick={() => runAiEdit(p.prompt)}
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                </div>
                                <textarea
                                    className="img-studio-prompt"
                                    rows={3}
                                    maxLength={aiMode === 'draft' ? 180 : 400}
                                    value={prompt}
                                    disabled={busy}
                                    placeholder="e.g. Clean white Tabex studio background, soft shadow, keep product exact…"
                                    onChange={(e) => setPrompt(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                            e.preventDefault();
                                            runAiEdit();
                                        }
                                    }}
                                />
                                <div className="img-studio-prompt-meta">
                                    {prompt.length}/{aiMode === 'draft' ? 180 : 400} · {aiMode === 'draft' ? '512px draft' : '1024px final'}
                                </div>
                                <button
                                    type="button"
                                    className="img-studio-primary"
                                    disabled={busy || !prompt.trim()}
                                    onClick={() => runAiEdit()}
                                >
                                    {busy ? 'Tabexawy is editing…' : 'Apply with Tabexawy'}
                                </button>
                            </div>
                        )}

                        {panel === 'crop' && (
                            <div className="img-studio-panel">
                                <p className="img-studio-panel-lead">Drag on the image to select a crop. Free — no AI tokens.</p>
                                <div className="img-studio-presets">
                                    {CROP_ASPECTS.map((a) => (
                                        <button
                                            key={a.id}
                                            type="button"
                                            className={`img-studio-chip${cropAspect === a.value ? ' is-on' : ''}`}
                                            disabled={busy}
                                            onClick={() => setCropAspect(a.value)}
                                        >
                                            {a.label}
                                        </button>
                                    ))}
                                </div>
                                <button type="button" className="img-studio-primary" disabled={busy} onClick={applyCrop}>
                                    Apply crop
                                </button>
                                <button
                                    type="button"
                                    className="img-studio-ghost-btn wide"
                                    disabled={busy}
                                    onClick={() => setCropRect(null)}
                                >
                                    Clear selection
                                </button>
                            </div>
                        )}

                        {panel === 'mark' && (
                            <div className="img-studio-panel">
                                <p className="img-studio-panel-lead">
                                    Mark what to change, then bake marks into the image. Free — no AI.
                                </p>
                                <div className="img-studio-btn-row img-studio-btn-row-wrap">
                                    {[
                                        ['pen', 'Pen'],
                                        ['rect', 'Box'],
                                        ['arrow', 'Arrow'],
                                        ['highlight', 'Highlight'],
                                        ['eraser', 'Eraser'],
                                    ].map(([id, label]) => (
                                        <button
                                            key={id}
                                            type="button"
                                            className={markTool === id ? 'is-on' : ''}
                                            disabled={busy}
                                            onClick={() => setMarkTool(id)}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <div className="img-studio-colors">
                                    {MARK_COLORS.map((c) => (
                                        <button
                                            key={c}
                                            type="button"
                                            className={`img-studio-swatch${markColor === c ? ' is-on' : ''}`}
                                            style={{ background: c }}
                                            disabled={busy}
                                            onClick={() => setMarkColor(c)}
                                            aria-label={c}
                                        />
                                    ))}
                                </div>
                                <label className="img-studio-slider">
                                    <span>
                                        Brush
                                        <em>{markSize}</em>
                                    </span>
                                    <input
                                        type="range"
                                        min={2}
                                        max={24}
                                        value={markSize}
                                        disabled={busy}
                                        onChange={(e) => setMarkSize(Number(e.target.value))}
                                    />
                                </label>
                                <button type="button" className="img-studio-primary" disabled={busy || !marks.length} onClick={bakeMarks}>
                                    Bake marks into image
                                </button>
                                <button
                                    type="button"
                                    className="img-studio-ghost-btn wide"
                                    disabled={busy || !marks.length}
                                    onClick={() => setMarks([])}
                                >
                                    Clear marks
                                </button>
                            </div>
                        )}

                        {panel === 'text' && (
                            <div className="img-studio-panel">
                                <p className="img-studio-panel-lead">
                                    Click the image to place text, drag to move. Bake, Download, or Use this image to flatten it into the photo.
                                </p>
                                <textarea
                                    className="img-studio-prompt"
                                    rows={3}
                                    value={selectedText ? selectedText.text : textDraft}
                                    disabled={busy}
                                    placeholder="Type text…"
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (selectedTextId) updateSelectedText({ text: v });
                                        else setTextDraft(v);
                                    }}
                                />
                                <div className="img-studio-presets">
                                    {TEXT_FONTS.map((f) => (
                                        <button
                                            key={f.id}
                                            type="button"
                                            className={`img-studio-chip${(selectedText?.font || textFont) === f.id ? ' is-on' : ''}`}
                                            disabled={busy}
                                            onClick={() => {
                                                setTextFont(f.id);
                                                updateSelectedText({ font: f.id });
                                            }}
                                        >
                                            {f.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="img-studio-colors">
                                    {TEXT_COLORS.map((c) => (
                                        <button
                                            key={c}
                                            type="button"
                                            className={`img-studio-swatch${(selectedText?.color || textColor) === c ? ' is-on' : ''}`}
                                            style={{ background: c }}
                                            disabled={busy}
                                            onClick={() => {
                                                setTextColor(c);
                                                updateSelectedText({
                                                    color: c,
                                                    strokeColor: c === '#ffffff' ? '#111827' : '#ffffff',
                                                });
                                            }}
                                            aria-label={c}
                                        />
                                    ))}
                                </div>
                                <label className="img-studio-slider">
                                    <span>
                                        Size
                                        <em>{selectedText?.size ?? textSize}%</em>
                                    </span>
                                    <input
                                        type="range"
                                        min={3}
                                        max={22}
                                        value={selectedText?.size ?? textSize}
                                        disabled={busy}
                                        onChange={(e) => {
                                            const v = Number(e.target.value);
                                            setTextSize(v);
                                            updateSelectedText({ size: v });
                                        }}
                                    />
                                </label>
                                <div className="img-studio-btn-row">
                                    {['left', 'center', 'right'].map((a) => (
                                        <button
                                            key={a}
                                            type="button"
                                            className={(selectedText?.align || textAlign) === a ? 'is-on' : ''}
                                            disabled={busy}
                                            onClick={() => {
                                                setTextAlign(a);
                                                updateSelectedText({ align: a });
                                            }}
                                        >
                                            {a}
                                        </button>
                                    ))}
                                </div>
                                <div className="img-studio-btn-row">
                                    <button
                                        type="button"
                                        className={(selectedText ? selectedText.bold : textBold) ? 'is-on' : ''}
                                        disabled={busy}
                                        onClick={() => {
                                            const v = selectedText ? !selectedText.bold : !textBold;
                                            setTextBold(v);
                                            updateSelectedText({ bold: v });
                                        }}
                                    >
                                        Bold
                                    </button>
                                    <button
                                        type="button"
                                        className={(selectedText ? selectedText.stroke : textStroke) ? 'is-on' : ''}
                                        disabled={busy}
                                        onClick={() => {
                                            const v = selectedText ? !selectedText.stroke : !textStroke;
                                            setTextStroke(v);
                                            updateSelectedText({ stroke: v });
                                        }}
                                    >
                                        Outline
                                    </button>
                                    <button
                                        type="button"
                                        disabled={busy || !selectedTextId}
                                        onClick={() => {
                                            setTextLayers((prev) => prev.filter((t) => t.id !== selectedTextId));
                                            setSelectedTextId(null);
                                        }}
                                    >
                                        Delete
                                    </button>
                                </div>
                                <button type="button" className="img-studio-primary" disabled={busy || !textLayers.length} onClick={bakeText}>
                                    Bake text into image
                                </button>
                                <button
                                    type="button"
                                    className="img-studio-ghost-btn wide"
                                    disabled={busy || !textLayers.length}
                                    onClick={() => {
                                        setTextLayers([]);
                                        setSelectedTextId(null);
                                    }}
                                >
                                    Clear all text
                                </button>
                            </div>
                        )}

                        {panel === 'adjust' && (
                            <div className="img-studio-panel">
                                {[
                                    ['brightness', 'Brightness', 40, 160],
                                    ['contrast', 'Contrast', 40, 160],
                                    ['saturate', 'Saturation', 0, 200],
                                    ['warmth', 'Warmth', -50, 50],
                                ].map(([key, label, min, max]) => (
                                    <label key={key} className="img-studio-slider">
                                        <span>
                                            {label}
                                            <em>{adjust[key]}</em>
                                        </span>
                                        <input
                                            type="range"
                                            min={min}
                                            max={max}
                                            value={adjust[key]}
                                            disabled={busy}
                                            onChange={(e) =>
                                                setAdjust((prev) => ({
                                                    ...prev,
                                                    [key]: Number(e.target.value),
                                                }))
                                            }
                                        />
                                    </label>
                                ))}
                                <button
                                    type="button"
                                    className="img-studio-ghost-btn wide"
                                    disabled={busy}
                                    onClick={() => setAdjust(DEFAULT_ADJUST)}
                                >
                                    Reset adjustments
                                </button>
                            </div>
                        )}

                        {panel === 'transform' && (
                            <div className="img-studio-panel">
                                <div className="img-studio-btn-row">
                                    <button type="button" disabled={busy} onClick={() => setRotation((r) => (r + 90) % 360)}>
                                        Rotate 90°
                                    </button>
                                    <button type="button" disabled={busy} onClick={() => setFlipH((v) => !v)}>
                                        Flip H
                                    </button>
                                    <button type="button" disabled={busy} onClick={() => setFlipV((v) => !v)}>
                                        Flip V
                                    </button>
                                </div>
                                <label className="img-studio-slider">
                                    <span>
                                        Zoom
                                        <em>{Math.round(zoom * 100)}%</em>
                                    </span>
                                    <input
                                        type="range"
                                        min={50}
                                        max={200}
                                        value={Math.round(zoom * 100)}
                                        disabled={busy}
                                        onChange={(e) => setZoom(Number(e.target.value) / 100)}
                                    />
                                </label>
                                <label className="img-studio-check">
                                    <input
                                        type="checkbox"
                                        checked={compare}
                                        disabled={busy}
                                        onChange={(e) => setCompare(e.target.checked)}
                                    />
                                    Compare with original
                                </label>
                            </div>
                        )}

                        {error ? <div className="img-studio-error">{error}</div> : null}
                        <div ref={overlayRef} className="img-studio-sr-only" aria-hidden="true" />
                    </aside>
                </div>

                <footer className="img-studio-footer">
                    <div className="img-studio-footer-cost">
                        {studioAiEnabled && lastCost ? (
                            <span>
                                Last Tabexawy edit: <strong>{formatCost(lastCost)}</strong>
                            </span>
                        ) : (
                            <span>Tabex Studio · local edits are free</span>
                        )}
                    </div>
                    <div className="img-studio-footer-right">
                        <button type="button" className="img-studio-ghost-btn" disabled={busy || studioClosing} onClick={requestClose}>
                            Cancel
                        </button>
                        <button type="button" className="img-studio-primary" disabled={busy} onClick={downloadCurrent}>
                            Download
                        </button>
                        {allowApply ? (
                            <button type="button" className="img-studio-primary" disabled={busy} onClick={handleApply}>
                                Use this image
                            </button>
                        ) : null}
                    </div>
                </footer>
            </div>
        </div>,
        document.body,
    );
}
